import { useState } from "react";
import {
  ConfirmDialog,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSearchSelect,
  FormSection,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import type { AssignableUser, MeetingParticipant } from "../../../api/meetingsApi";
import {
  MEETING_ROLE_LABELS,
  MEETING_ROLE_OPTIONS,
  PARTICIPANT_KIND_LABELS,
  PARTICIPANT_KIND_OPTIONS,
} from "../constants";

type Props = {
  participants?: MeetingParticipant[];
  users?: AssignableUser[];
  canManage: boolean;
  onChange: (participants: MeetingParticipant[]) => void;
  onSave?: () => void;
  saving?: boolean;
};

export function ParticipantsPanel({
  participants = [],
  users = [],
  canManage,
  onChange,
  onSave,
  saving,
}: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const [addForm, setAddForm] = useState({
    userId: "",
    meetingRole: "member",
    participantKind: "regular",
  });
  const [guestForm, setGuestForm] = useState({
    guestName: "",
    guestEmail: "",
    meetingRole: "guest",
    participantKind: "guest",
  });

  const handleAddUser = () => {
    if (!addForm.userId) return;
    const u = users.find((x) => String(x.id) === String(addForm.userId));
    if (!u) return;
    if (participants.some((p) => p.userId && String(p.userId) === String(u.id))) return;
    onChange([
      ...participants,
      {
        userId: u.id,
        participantKind: addForm.participantKind,
        meetingRole: addForm.meetingRole,
        displayName: u.name,
      },
    ]);
    setAddForm({ userId: "", meetingRole: "member", participantKind: "regular" });
    setAddOpen(false);
  };

  const handleAddGuest = () => {
    if (!guestForm.guestName.trim()) return;
    onChange([
      ...participants,
      {
        guestName: guestForm.guestName.trim(),
        guestEmail: guestForm.guestEmail.trim() || null,
        participantKind: "guest",
        meetingRole: guestForm.meetingRole,
        displayName: guestForm.guestName.trim(),
      },
    ]);
    setGuestForm({
      guestName: "",
      guestEmail: "",
      meetingRole: "guest",
      participantKind: "guest",
    });
    setGuestOpen(false);
  };

  const removeAt = (idx: number) => {
    onChange(participants.filter((_, i) => i !== idx));
    setDeleteIdx(null);
  };

  const updateRole = (idx: number, meetingRole: string) => {
    const next = [...participants];
    next[idx] = { ...next[idx], meetingRole };
    onChange(next);
  };

  return (
    <div className="tm-panel">
      <div className="tm-panel__head">
        <h3 className="tm-panel__title">משתתפים ({participants.length})</h3>
        {canManage ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="tm-btn tm-btn--outline tm-btn--sm"
              onClick={() => setAddOpen(true)}
            >
              + חבר צוות
            </button>
            <button
              type="button"
              className="tm-btn tm-btn--outline tm-btn--sm"
              onClick={() => setGuestOpen(true)}
            >
              + אורח
            </button>
            {onSave ? (
              <button
                type="button"
                className="tm-btn tm-btn--primary tm-btn--sm"
                onClick={onSave}
                disabled={saving}
              >
                {saving ? "שומר…" : "שמור משתתפים"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {!participants.length ? (
        <div className="tm-empty">טרם הוגדרו משתתפים</div>
      ) : (
        <div className="tm-chips">
          {participants.map((p, idx) => (
            <div key={p.id || `${p.userId ?? ""}-${p.guestName ?? idx}`} className="tm-chip">
              <span>{p.displayName || p.guestName || "משתתף"}</span>
              <span className="tm-chip__role">
                {MEETING_ROLE_LABELS[p.meetingRole] || p.meetingRole}
              </span>
              <span
                className={`tm-chip__kind${p.participantKind === "guest" ? " tm-chip__kind--guest" : ""}`}
              >
                {PARTICIPANT_KIND_LABELS[p.participantKind] || p.participantKind}
              </span>
              {canManage ? (
                <>
                  <FormField
                    type="select"
                    value={p.meetingRole}
                    onChange={(value) => updateRole(idx, value)}
                    options={MEETING_ROLE_OPTIONS}
                  />
                  <button
                    type="button"
                    className="tm-icon-btn tm-icon-btn--danger"
                    onClick={() => setDeleteIdx(idx)}
                    aria-label="הסר"
                  >
                    ×
                  </button>
                </>
              ) : null}
            </div>
          ))}
        </div>
      )}
      <FormDialog open={addOpen} onClose={() => setAddOpen(false)} size="sm">
        <FormCard
          title="הוספת משתתף קבוע"
          onSave={handleAddUser}
          onCancel={() => setAddOpen(false)}
          saveLabel="הוסף"
          cancelLabel="ביטול"
        >
          <FormSection title="בחירת משתמש">
            <FormGrid cols={1}>
              <FormSearchSelect
                label="משתמש"
                options={users}
                value={users.find((u) => String(u.id) === String(addForm.userId)) || null}
                onChange={(v) =>
                  setAddForm((f) => ({
                    ...f,
                    userId: (v as AssignableUser | null)?.id || "",
                  }))
                }
                getOptionLabel={(o) => (o as AssignableUser).name}
                isOptionEqual={(a, b) =>
                  (a as AssignableUser | null)?.id === (b as AssignableUser | null)?.id
                }
                placeholder="בחר משתמש…"
              />
              <FormField
                label="תפקיד בישיבה"
                value={addForm.meetingRole}
                onChange={(v) => setAddForm((f) => ({ ...f, meetingRole: v }))}
                options={MEETING_ROLE_OPTIONS}
              />
              <FormField
                label="סוג משתתף"
                value={addForm.participantKind}
                onChange={(v) => setAddForm((f) => ({ ...f, participantKind: v }))}
                options={PARTICIPANT_KIND_OPTIONS}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
      <FormDialog open={guestOpen} onClose={() => setGuestOpen(false)} size="sm">
        <FormCard
          title="הוספת אורח"
          onSave={handleAddGuest}
          onCancel={() => setGuestOpen(false)}
          saveLabel="הוסף"
          cancelLabel="ביטול"
        >
          <FormSection title="פרטי אורח">
            <FormGrid cols={1}>
              <FormField
                label="שם"
                required
                value={guestForm.guestName}
                onChange={(v) => setGuestForm((f) => ({ ...f, guestName: v }))}
              />
              <FormField
                label="אימייל"
                type="email"
                value={guestForm.guestEmail}
                onChange={(v) => setGuestForm((f) => ({ ...f, guestEmail: v }))}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
      <ConfirmDialog
        open={deleteIdx !== null}
        onClose={() => setDeleteIdx(null)}
        onConfirm={() => {
          if (deleteIdx !== null) removeAt(deleteIdx);
        }}
        title="הסרת משתתף"
        message="להסיר את המשתתף מהישיבה?"
        confirmText="הסר"
        cancelText="ביטול"
      />
    </div>
  );
}
